import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "@/store/auth.store";
import type { ErrorEnvelope, SuccessEnvelope } from "@/types/common";
import type { RefreshResponse } from "@/types/auth";

/**
 * Single centralized HTTP client. Every API module in src/api/*.api.ts must
 * import this instance — no page/component is allowed to call axios/fetch
 * directly (see admin build rules, section 16).
 *
 * All requests go through the existing NestJS API Gateway (VITE_API_URL),
 * which proxies to the individual backend microservices. This client never
 * talks to Postgres/Redis/Kafka directly.
 *
 * Every gateway response is wrapped by ResponseEnvelopeInterceptor as
 * `{ success: true, data, message }` (or `{ success:false, error }` on
 * failure, handled by AllExceptionsFilter). The response interceptor below
 * unwraps `data` so every api/*.ts module can keep treating `r.data` as the
 * actual payload.
 */
const baseURL = import.meta.env.VITE_API_URL ?? "http://localhost:3000/api/v1";

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) return null;

  try {
    const res = await axios.post<SuccessEnvelope<RefreshResponse>>(
      `${baseURL}/auth/refresh`,
      { refreshToken },
    );
    const tokens = res.data.data;
    setTokens(tokens.accessToken, tokens.refreshToken);
    return tokens.accessToken;
  } catch {
    clear();
    return null;
  }
}

apiClient.interceptors.response.use(
  // Unwrap the { success, data, message } envelope so callers just see `data`.
  (response) => {
    const body = response.data as SuccessEnvelope<unknown> | unknown;
    if (body && typeof body === "object" && (body as SuccessEnvelope<unknown>).success === true) {
      response.data = (body as SuccessEnvelope<unknown>).data;
    }
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // The login request rejecting with 401 means the credentials were wrong,
    // not that a session expired — there's no token to refresh, and forcing
    // a refresh-then-redirect here would reload the page out from under the
    // form before the inline error can render. Let it reject normally so
    // Login.tsx's own error state handles it.
    // The same goes for the public reset endpoints and change-password, whose
    // 401 means a wrong current password rather than an expired session.
    const isLoginRequest = /\/auth\/admin\/(login|forgot-password|reset-password|change-password)/.test(
      original?.url ?? "",
    );

    if (error.response?.status === 401 && original && !original._retry && !isLoginRequest) {
      original._retry = true;
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
      // Refresh failed — force sign-out, redirect handled by ProtectedRoute.
      window.location.assign("/login");
    }

    return Promise.reject(error);
  },
);

/** Extracts a human-readable message from the backend's { success:false, error } envelope. */
export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Partial<ErrorEnvelope> | undefined;
    if (data?.error?.message) return data.error.message;
    return error.message;
  }
  return error instanceof Error ? error.message : "Unexpected error";
}
